import { useRouter } from 'next/router';
import React, { createContext, useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import useIsMaxScreen from '@/hooks/useIsMaxScreen';
import useIsMediumScreen from '@/hooks/useIsMediumScreen';
import useMount from '@/hooks/useMount';
import {
  GetResourceListRequest,
  GetResourceListResponse,
} from '@/request/interface/resources';
import { getResourceList } from '@/request/resources';
import useResourcesStore from '@/store/useResourcesStore';
import { capitalizeFirstLetter } from '@/utils/utils';

import ToastLoading from '../ToastLoading';

import { RootMenu, SubMenu } from './types';
import { findMenusByString } from './utils';

export interface Tag extends SubMenu {}

export interface Query {
  order?: 'publish' | 'view';
  page?: string;
}

interface ResourceContextProps {
  filterTags: Tag[];
  pushFilterTagAndUpdatePath: (tag: Tag) => void;
  setFilterTagsAndUpdatePath: (tags: Tag[]) => void;
  deleteFilterTagAndUpdatePath: (tag: Tag) => void;
  query: Query;
  setQueryAndUpdatePath: (query: Query) => void;
  setQuery: (query: Query) => void;
  resourceList: GetResourceListResponse | undefined;
}

interface ResourceProviderProps {
  children: React.ReactElement;
  menus: RootMenu[];
}

interface QueryParams {
  order?: 'publish' | 'view';
  page?: string;
}

const ResourceContext = createContext<ResourceContextProps>({
  filterTags: [],
  pushFilterTagAndUpdatePath: () => {},
  setFilterTagsAndUpdatePath: () => {},
  deleteFilterTagAndUpdatePath: () => {},
  query: {},
  setQueryAndUpdatePath: () => {},
  setQuery: () => {},
  resourceList: undefined,
});

export const ResourceProvider = ({
  children,
  menus,
}: ResourceProviderProps) => {
  const router = useRouter();
  const { subject, tags, order, page } = router.query;
  const currentMenus = findMenusByString(tags as string, menus);
  const { setFilterTags, filterTags } = useResourcesStore(state => {
    return { setFilterTags: state.setFilterTags, filterTags: state.filterTags };
  });
  const [query, setQuery] = useState<QueryParams>({
    order: (order ? (order as 'publish' | 'view') : 'view') as
      | 'publish'
      | 'view',
    ...(page ? { page: page as string } : {}),
  });
  const [resourceList, setResourceList] = useState<GetResourceListResponse>();
  const isMaxScreen = useIsMaxScreen();
  const isMediumScreen = useIsMediumScreen();

  // 基于 menus 构建稳定的父级/子级顺序映射（menus 变化时才重建）
  const { parentOrderMap, childOrderMap } = useMemo(() => {
    const pMap = new Map<string, number>();
    menus.forEach((parent, index) => {
      pMap.set(parent.value, index);
    });
    const cMap: Record<string, Map<string, number>> = {};
    menus.forEach(parent => {
      const map = new Map<string, number>();
      parent.subMenu?.forEach((child, idx) => {
        map.set(child.value, idx);
      });
      cMap[parent.value] = map;
    });
    return { parentOrderMap: pMap, childOrderMap: cMap };
  }, [menus]);

  const orderTagsByMenu = (list: Tag[]) => {
    if (!Array.isArray(list) || !list.length) {
      return list;
    }
    const safeGet = (
      val: number | undefined,
      fallback = Number.MAX_SAFE_INTEGER,
    ) => (typeof val === 'number' ? val : fallback);
    const ordered = [...list].sort((a, b) => {
      const aParent = safeGet(parentOrderMap.get(a.parentValue as string));
      const bParent = safeGet(parentOrderMap.get(b.parentValue as string));
      if (aParent !== bParent) {
        return aParent - bParent;
      }
      const aChild = safeGet(
        childOrderMap[a.parentValue as string]?.get(a.value),
      );
      const bChild = safeGet(
        childOrderMap[b.parentValue as string]?.get(b.value),
      );
      return aChild - bChild;
    });
    return ordered;
  };

  useMount(() => {
    if (!currentMenus?.length && tags) {
      router.push(`/resources/${subject}/${window.location.search}`);
    }
    // 初始化：保持 UI 选中顺序与来源一致（不做排序），仅 URL 层面在 updateUrl 时排序
    setFilterTags((currentMenus as Tag[]) || []);
  });

  const getPageSize = () => {
    if (isMaxScreen) {
      return 16;
    }
    if (isMediumScreen) {
      return 12;
    }
    return 5;
  };

  const getResources = async (queryParams: QueryParams, tagList: Tag[]) => {
    const orderMap = {
      publish: 'LATEST_RELEASES',
      view: 'MOST_VIEWED',
    };
    const params = {
      sortingRule: orderMap[queryParams.order as 'publish' | 'view'],
      tagIdList: tagList
        ?.filter(item => item.menuType === 'Tag')
        .map(({ id }) => id),
      themeId: tagList?.find(item => item.menuType === 'Theme')?.id,
      topicId: tagList?.find(item => item.menuType === 'Topic')?.id,
      pageNum: Number(queryParams.page || 1),
      pageSize: getPageSize(),
      subject: capitalizeFirstLetter(subject as string),
    };
    getResourceList(params as GetResourceListRequest)
      .then(res => {
        setResourceList(res);
      })
      .finally(() => {
        ToastLoading.hide();
      });
  };

  const updateUrl = (
    currentTags: Tag[],
    queryParam: {
      order?: 'publish' | 'view' | undefined;
      page?: string;
    },
  ) => {
    // 仅用于 URL：统一按左侧菜单顺序稳定排序（使用 memo 的顺序映射）
    const orderedTagsForUrl = orderTagsByMenu(currentTags);

    // UI 与数据请求使用用户的点击顺序，保持交互习惯
    setFilterTags(currentTags);
    setQuery(queryParam);
    getResources(queryParam, currentTags);

    const tagValues = orderedTagsForUrl?.map(item => item.value);

    const tagPath = tagValues?.join('-');
    if (queryParam?.page === '1') {
      // eslint-disable-next-line no-param-reassign
      delete queryParam?.page;
    }
    if (queryParam?.order === undefined) {
      // eslint-disable-next-line no-param-reassign
      delete queryParam?.order;
    }
    let params = '';
    if (queryParam && Object.keys(queryParam)?.length) {
      params = new URLSearchParams(queryParam).toString();
    }
    router.push(
      `/resources/${subject}${tagPath ? `/${tagPath}` : ''}/${params ? `?${params}` : ''}`,
      undefined,
      {
        scroll: false,
        shallow: true,
      },
    );
  };

  useMount(() => {
    getResources(query, (currentMenus as Tag[]) || []);
  });

  useUpdateEffect(() => {
    getResources(query, (currentMenus as Tag[]) || []);
  }, [subject]);

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const providerValue = {
    filterTags,
    pushFilterTagAndUpdatePath: (tag: Tag) => {
      const tagList = filterTags.filter(
        item => item.parentValue !== tag.parentValue,
      );
      const currentTags = [...tagList, tag];
      const newQuery = { ...query, page: '1' };
      updateUrl(currentTags, newQuery);
    },
    setFilterTagsAndUpdatePath: (currentTags: Tag[]) => {
      const newQuery = { ...query, page: '1' };
      updateUrl(currentTags, newQuery);
    },
    deleteFilterTagAndUpdatePath: (tag: Tag) => {
      const currentTags = filterTags.filter(item => item.value !== tag.value);
      const newQuery = { ...query, page: '1' };
      updateUrl(currentTags, newQuery);
    },
    query,
    setQueryAndUpdatePath: (value: Query) => {
      const newQuery = { ...query, ...value };
      updateUrl(filterTags, newQuery);
    },
    setQuery: (value: Query) => {
      const newQuery = { ...query, ...value };
      setQuery(newQuery);
    },
    resourceList: resourceList || undefined,
  };

  return (
    <ResourceContext.Provider value={providerValue}>
      {children}
    </ResourceContext.Provider>
  );
};

export default ResourceContext;
